import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import React from 'react';

import {assert} from 'Asserts';
import SearchableText from 'notebook/mediocre/search/SearchableText';
import {
  forwardSearchResultProps,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import * as TableConstants from 'notebook/tables/TableConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import FloatTooltip from 'ui-library/floating-ui/Tooltip';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import TableCollapserChevron from 'ui-library/tables/TableCollapserChevron';
import {ElementMouseEvent} from 'util/Types';

export interface TableTitleBarProps extends MediocreSearchResultProps {
  // Actions on the right side of the header such as `Download as CSV`.
  rightHeaderControls?: JSX.Element | null;
  onHeaderControlsMouseDown?: () => void;
  icon?: BenchlingIconName | null;
  isTableExpanded: boolean;
  isTableMaximized: boolean;
  isTableSplitScreen: boolean;
  // A table is focused when it is currently opened in the editor an this is the collapsed focused placeholder
  isFocused: boolean;
  disableMaximize?: boolean;
  disableSplitScreen?: boolean;
  viewer?: boolean;
  isLoading?: boolean;
  // Actions to the left of the table name such as `Add rows` menu.
  leftHeaderControls?: JSX.Element | null;
  useRoundedTopBorder: boolean;
  useRoundedBottomBorder: boolean;
  // Left padding
  tableIndentationPx: number;
  // Padding can be included on the right side if requested. This is needed by (eg) AlwaysCollapsedTable
  forceRightPadding?: boolean;
  // The header of the table may be combined of `tableTypeLabel` and `tableName`. If
  // both are present, `tableTypeLabel` would represent the type of the table, such as `Transfer`
  // and `tableName` would be the unique name of the table.
  tableTypeLabel?: string;
  tableName: string | JSX.Element;
  shouldRenderCollapserChevron: boolean;
  usePointerCursor: boolean;
  // If `tooltipText` is specified, the title bar is wrapped with a tooltip with `tooltipText`.
  tooltipText?: string | null;
  // If `titleTooltip` is specified, the table's title (name) is wrapped with a tooltip with `titleTooltip`. This is separate from `tooltipText`.
  titleTooltip?: string | null;

  // Action to invoke on right click on the title bar.
  onOpenContextMenu?: (e: ElementMouseEvent) => void;
  // Action to invoke when title is clicked.
  onTitleBarClick?: () => void;
  onTableNameRefChanged?: (node: HTMLDivElement | null) => void;
  // Action to invoke when `TableCollapserChevron` is clicked.
  onToggleCollapsed?: () => void;
  onMaximize?: () => void;
  onSplitScreen?: () => void;
  onClose?: () => void;
  shouldIncreaseCollapserSpacing: boolean;
  isTableHeaderFaded?: boolean;
  dataTestAttrs?: DataTestAttrs;
  status: TableConstants.TableIndicatorStatus;
}

/**
 * Title bar of a Notebook table
 */
export default class TableTitleBar extends React.Component<TableTitleBarProps> {
  static defaultProps = {
    useRoundedTopBorder: true,
    useRoundedBottomBorder: false,
    shouldIncreaseCollapserSpacing: false,
  };

  rightHeaderControlsRef = React.createRef<HTMLDivElement>();
  tableViewControlsRef = React.createRef<HTMLDivElement>();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleTableClick = (e: any): void => {
    if (
      (this.rightHeaderControlsRef.current != null &&
        $.contains(this.rightHeaderControlsRef.current, e.target)) ||
      (this.tableViewControlsRef.current != null && $.contains(this.tableViewControlsRef.current, e.target))
    ) {
      e.stopPropagation();
      if (this.props.onHeaderControlsMouseDown) {
        this.props.onHeaderControlsMouseDown();
      }
      return;
    }
    if (this.props.onTitleBarClick != null) {
      e.stopPropagation();
      e.preventDefault();
      this.props.onTitleBarClick();
    }
  };

  handleDoubleClick = (): void => {
    if (this.props.isTableMaximized && this.props.onClose) {
      this.props.onClose();
    } else if (this.props.onToggleCollapsed) {
      this.props.onToggleCollapsed();
    }
  };

  _renderFloatingControls(): JSX.Element | null {
    if (this.props.shouldRenderCollapserChevron && !this.props.isFocused) {
      if (this.props.onToggleCollapsed == null) {
        assert(false, 'onToggleCollapsed is expected when shouldRenderCollapserChevron is true');
      }
      return (
        <div
          className={classNames(
            'u-flex-none u-tiny-spacing-left u-vertical-center-items table-floater table-collapse-control',
            {
              [css(styles.increasedCollapserSpacing)]: this.props.shouldIncreaseCollapserSpacing,
            }
          )}
        >
          <TableCollapserChevron
            isTableExpanded={this.props.isTableExpanded}
            onToggleCollapsed={this.props.onToggleCollapsed}
          />
        </div>
      );
    }
    return null;
  }

  override render(): JSX.Element {
    return (
      <div>
        {this.props.tooltipText && (
          <Tooltip title={this.props.tooltipText} delay={TableConstants.TABLE_TOOLTIP_DELAY}>
            <div
              className={classNames('hover-area-for-table-tooltip')}
              style={{height: TableConstants.TABLE_HEADER_HEIGHT}}
              {...getTestProps({component: 'TableTitleBar', element: 'tooltipHoverArea'})}
            />
          </Tooltip>
        )}
        <div
          className={classNames(
            'mediocre-tableEditable-titleBar',
            this.props.forceRightPadding && 'u-small-padding-right'
          )}
          style={{
            height: TableConstants.TABLE_HEADER_HEIGHT,
            paddingLeft: this.props.tableIndentationPx,
          }}
          {...getTestProps({component: 'TableTitleBar', element: 'root'})}
        >
          <div
            ref={this.props.onTableNameRefChanged}
            className={classNames('mediocre-tableEditable-tableName', {
              'table-roundedTopCorners': this.props.useRoundedTopBorder,
              'table-roundedBottomCorners': this.props.useRoundedBottomBorder,
              'mediocre-tableEditable-tableName--focused': this.props.isFocused,
              'mediocre-table-row-status--rowIsOutdated':
                this.props.status === TableConstants.TableIndicatorStatus.IMMUTABLE ||
                this.props.status === TableConstants.TableIndicatorStatus.OUT_OF_DATE,
              'mediocre-table-row-status--rowIsArchived':
                this.props.status === TableConstants.TableIndicatorStatus.ROWS_ARCHIVED ||
                this.props.status === TableConstants.TableIndicatorStatus.TABLE_OR_SCHEMA_ARCHIVED,
              'mediocre-table-row-status--rowHasUnsubmittedChanges':
                this.props.status === TableConstants.TableIndicatorStatus.UNSUBMITTED_CHANGES,
              'mediocre-table-row-status--rowHasErrors':
                this.props.status === TableConstants.TableIndicatorStatus.ERROR ||
                this.props.status === TableConstants.TableIndicatorStatus.HAS_BYPASSABLE_ERRORS,
              'u-pointer': this.props.usePointerCursor,
            })}
            onDoubleClick={this.handleDoubleClick}
            onMouseDown={this.handleTableClick}
            onContextMenu={this.props.onOpenContextMenu}
          >
            <div className="u-flex-grow u-overflow-hidden u-vertical-center-items">
              {this.props.icon && (
                <BenchlingIcon
                  className={classNames(
                    'mediocre-tableEditable-tableIcon',
                    this.props.isTableHeaderFaded && css(styles.fadedHeader)
                  )}
                  icon={this.props.icon}
                />
              )}
              <div className={classNames(this.props.isTableHeaderFaded && css(styles.fadedHeader))}>
                {this.props.tableTypeLabel}
              </div>
              {this.props.tableName ? (
                <FloatTooltip text={this.props.titleTooltip || ''} isDisabled={!this.props.titleTooltip}>
                  <div
                    className={classNames(
                      'u-ellipsis-text',
                      {
                        // only style this in grey if there is a table label
                        'mediocre-tableEditable-tableTitle': this.props.tableTypeLabel != null,
                      },
                      this.props.isTableHeaderFaded && css(styles.fadedHeader)
                    )}
                    {...getTestProps(this.props.dataTestAttrs)}
                  >
                    <span className="u-ellipsis-text">
                      {typeof this.props.tableName === 'string' ? (
                        <SearchableText
                          text={this.props.tableName}
                          {...forwardSearchResultProps(this.props)}
                        />
                      ) : (
                        this.props.tableName
                      )}
                    </span>
                  </div>
                </FloatTooltip>
              ) : (
                <Tooltip
                  title={this.props.titleTooltip || ''}
                  disabled={!this.props.titleTooltip}
                  delay={TableConstants.TABLE_TOOLTIP_DELAY}
                >
                  {this.props.tableName && (
                    <div
                      className={classNames(
                        'u-ellipsis-text',
                        {
                          // only style this in grey if there is a table label
                          'mediocre-tableEditable-tableTitle': this.props.tableTypeLabel != null,
                        },
                        this.props.isTableHeaderFaded && css(styles.fadedHeader)
                      )}
                      {...getTestProps(this.props.dataTestAttrs)}
                    >
                      <span className="u-ellipsis-text">
                        {typeof this.props.tableName === 'string' ? (
                          <SearchableText
                            text={this.props.tableName}
                            {...forwardSearchResultProps(this.props)}
                          />
                        ) : (
                          this.props.tableName
                        )}
                      </span>
                    </div>
                  )}
                </Tooltip>
              )}

              {this.props.isLoading === true && (
                <span className="u-padding-left">
                  <HorizontalThrobber />
                </span>
              )}
              {this.props.leftHeaderControls}
            </div>
            <div className="u-flex-none" ref={this.rightHeaderControlsRef}>
              {this.props.rightHeaderControls}
            </div>
            <div
              ref={this.tableViewControlsRef}
              className={classNames('u-mini-spacing-left', {
                'u-small-spacing-right-negative': this.props.isTableMaximized,
              })}
            >
              {this.props.onSplitScreen &&
                (this.props.isFocused || !this.props.isTableSplitScreen) &&
                !this.props.disableSplitScreen &&
                !this.props.disableMaximize &&
                !this.props.viewer && (
                  <Tooltip title="Enter split screen">
                    <Button
                      dataTestAttrs={{component: 'TableTitleBar', element: 'splitScreenButton'}}
                      textColor={this.props.isTableSplitScreen ? Colors.BLUE_LIGHT : Colors.GRAY_DARKER}
                      disabled={this.props.isTableSplitScreen}
                      buttonStyle={ButtonStyle.TRANSPARENT}
                      icon="dock-bottom"
                      size={ButtonSize.MINI}
                      onClick={this.props.onSplitScreen}
                    />
                  </Tooltip>
                )}
              {this.props.onMaximize &&
                (this.props.isFocused || !this.props.isTableMaximized) &&
                !this.props.disableMaximize &&
                !this.props.viewer &&
                (!this.props.disableSplitScreen || !this.props.isTableMaximized) && (
                  <Tooltip title="Enter full screen">
                    <Button
                      dataTestAttrs={{component: 'TableTitleBar', element: 'maximizeButton'}}
                      textColor={this.props.isTableMaximized ? Colors.BLUE_LIGHT : Colors.GRAY_DARKER}
                      disabled={this.props.isTableMaximized}
                      buttonStyle={ButtonStyle.TRANSPARENT}
                      icon="expand-right-full"
                      size={ButtonSize.MINI}
                      onClick={this.props.onMaximize}
                    />
                  </Tooltip>
                )}
              {this.props.onClose &&
                !this.props.isFocused &&
                (this.props.isTableMaximized || this.props.isTableSplitScreen) && (
                  <Tooltip title={this.props.isTableMaximized ? 'Close full screen' : 'Close split screen'}>
                    <Button
                      dataTestAttrs={{component: 'TableTitleBar', element: 'closeButton'}}
                      textColor={Colors.GRAY_DARKER}
                      disabled={false}
                      text="Close"
                      buttonStyle={ButtonStyle.TRANSPARENT}
                      icon="close"
                      size={ButtonSize.MINI}
                      onClick={this.props.onClose}
                    />
                  </Tooltip>
                )}
            </div>
          </div>
          {this._renderFloatingControls()}
        </div>
      </div>
    );
  }
}

const styles = StyleSheet.create({
  increasedCollapserSpacing: {marginLeft: '15px'},
  fadedHeader: {opacity: 0.5},
});
